import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import ConversationTranscript from '../layout/ConversationTranscript';

interface MessageBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  isMobileView: boolean;
}

const MessageBottomSheet: React.FC<MessageBottomSheetProps> = ({
  isOpen,
  onClose,
  isMobileView
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setShouldRender(true);
      // Start animation after render
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      document.body.style.overflow = 'auto';
      setIsAnimating(false);
      // Remove from DOM after animation completes
      setTimeout(() => setShouldRender(false), 300);
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-background-light dark:bg-background-dark backdrop-blur-sm transition-opacity duration-300 ease-out ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      
      {/* Bottom Sheet */}
      <div className={`relative flex flex-col bg-surface-light/40 dark:bg-surface-dark/40 backdrop-blur-sm w-full h-full transition-transform duration-300 ease-out ${
        isAnimating ? 'translate-y-0' : 'translate-y-full'
      }`}>
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-border-light dark:border-border-dark backdrop-blur-sm">
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Messages
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-background-light/60 dark:hover:bg-background-dark/60 transition-colors"
            aria-label="Close messages"
          >
            <X className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
          </button>
        </div>

        {/* Content Area - Full height conversation transcript */}
        <div className="flex-1 min-h-0">
          <ConversationTranscript isMobileView={isMobileView} />
        </div>
      </div>
    </div>
  );
};

export default MessageBottomSheet; 